import React, { createContext, useCallback, useContext, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { markDashboardOpening } from "../../js/helpers/dashboardTransition";
import { saveUserBootstrap } from "../../js/helpers/userBootstrap";
import { prefetchAppBundle } from "./prefetchPipeline";

const DashboardOpenContext = createContext(null);

export function DashboardOpenProvider({ children }) {
    const [opening, setOpening] = useState(false);
    const user = useSelector((state) => state.userReducer?.user);

    const openDashboard = useCallback((event) => {
        event?.preventDefault();
        prefetchAppBundle({ priority: "preload" });
        if (user?.id) {
            saveUserBootstrap(user);
        }
        markDashboardOpening();
        setOpening(true);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                window.location.assign("/dashboard");
            });
        });
    }, [user]);

    return (
        <DashboardOpenContext.Provider value={{ openDashboard, opening }}>
            {children}
            {opening ? <DashboardOpeningOverlay /> : null}
        </DashboardOpenContext.Provider>
    );
}

export function useDashboardOpen() {
    const ctx = useContext(DashboardOpenContext);
    if (!ctx) {
        throw new Error("useDashboardOpen must be used within DashboardOpenProvider");
    }
    return ctx;
}

const warmDashboard = () => prefetchAppBundle({ priority: "preload" });

export function DashboardLink({ className, children, onBeforeOpen, ...props }) {
    const { openDashboard } = useDashboardOpen();

    return (
        <a
            href="/dashboard"
            className={className}
            onMouseEnter={warmDashboard}
            onFocus={warmDashboard}
            onTouchStart={warmDashboard}
            onClick={(event) => {
                onBeforeOpen?.();
                openDashboard(event);
            }}
            {...props}
        >
            {children}
        </a>
    );
}

function DashboardOpeningOverlay() {
    return ReactDOM.createPortal(
        <div className="clk-dashboard-open-overlay" role="status" aria-live="polite" aria-busy="true">
            <div className="clk-dashboard-open-card">
                <div className="clk-dashboard-open-spinner" aria-hidden="true" />
                <p className="clk-dashboard-open-text">Opening dashboard…</p>
            </div>
        </div>,
        document.body
    );
}
